// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface Sport {
  id: string;
  name: string;
  gender: string | null;
}

interface Position {
  id: string;
  sport_id: string;
  position_name: string;
}

const PositionsManagement: React.FC = () => {
  const [sports, setSports] = useState<Sport[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPositionsLoading, setIsPositionsLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPositionName, setNewPositionName] = useState('');

  useEffect(() => {
    console.log('🔍 Positions Admin Loaded - PositionsManagement.tsx');
    fetchSports();
  }, []);

  useEffect(() => {
    if (selectedSportId) {
      console.log('🔍 Selected sport changed:', selectedSportId);
      fetchPositionsBySport(selectedSportId);
    } else {
      setPositions([]);
    }
  }, [selectedSportId]);

  const fetchSports = async () => {
    try {
      setIsLoading(true);
      console.log('🔍 Fetching sports for dropdown...');
      
      const { data, error } = await supabase
        .from('sports')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) {
        console.error('❌ Error fetching sports:', error);
        toast.error('Failed to load sports');
        return;
      }

      console.log('✅ Sports fetched successfully:', data?.length || 0, 'records');
      setSports(data || []);
    } catch (error) {
      console.error('❌ Unexpected error fetching sports:', error);
      toast.error('Failed to load sports');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPositionsBySport = async (sportId: string) => {
    try {
      setIsPositionsLoading(true);
      console.log('🔍 Fetching positions for sport:', sportId);
      
      const { data, error } = await supabase
        .from('positions')
        .select('*')
        .eq('sport_id', sportId)
        .order('position_name', { ascending: true });

      if (error) {
        console.error('❌ Error fetching positions:', error);
        toast.error('Failed to load positions');
        return;
      }

      console.log('✅ Positions fetched successfully:', data?.length || 0, 'records');
      setPositions(data || []);
    } catch (error) {
      console.error('❌ Unexpected error fetching positions:', error);
      toast.error('Failed to load positions');
    } finally {
      setIsPositionsLoading(false);
    }
  };

  const handleAddPosition = async () => {
    if (!newPositionName.trim()) {
      toast.error('Position name is required');
      return;
    }

    if (!selectedSportId) {
      toast.error('Please select a sport first');
      return;
    }

    console.log('🔍 Attempting to add position:', {
      position_name: newPositionName.trim(),
      sport_id: selectedSportId
    });

    setIsSubmitting(true);
    try {
      const insertData = {
        position_name: newPositionName.trim(),
        sport_id: selectedSportId,
      };

      console.log('🔍 Insert data prepared:', insertData);

      const { data, error } = await supabase
        .from('positions')
        .insert([insertData])
        .select()
        .single();

      console.log('🔍 Supabase insert result:', { data, error });

      if (error) {
        console.error('❌ Error adding position:', error);
        toast.error(`Failed to add position: ${error.message}`);
        return;
      }

      console.log('✅ Position added successfully');
      await fetchPositionsBySport(selectedSportId);
      setNewPositionName('');
      setIsAddModalOpen(false);
      toast.success('Position added successfully');
    } catch (error) {
      console.error('❌ Unexpected error adding position:', error);
      toast.error('Failed to add position');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditPosition = async () => {
    if (!editingPosition || !newPositionName.trim()) {
      toast.error('Position name is required');
      return;
    }

    console.log('🔍 Attempting to update position:', editingPosition.id, 'with name:', newPositionName.trim());

    setIsSubmitting(true);
    try {
      const updateData = {
        position_name: newPositionName.trim(),
      };

      const { data, error } = await supabase
        .from('positions')
        .update(updateData)
        .eq('id', editingPosition.id)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating position:', error);
        toast.error('Failed to update position');
        return;
      }

      console.log('✅ Position updated successfully');
      setPositions(positions.map(position => 
        position.id === editingPosition.id ? data : position
      ));
      setNewPositionName('');
      setEditingPosition(null);
      setIsEditModalOpen(false);
      toast.success('Position updated successfully');
    } catch (error) {
      console.error('❌ Unexpected error updating position:', error);
      toast.error('Failed to update position');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePosition = async (positionId: string) => {
    console.log('🔍 Attempting to delete position:', positionId);
    try {
      const { error } = await supabase
        .from('positions')
        .delete()
        .eq('id', positionId);

      if (error) {
        console.error('❌ Error deleting position:', error);
        toast.error('Failed to delete position');
        return;
      }

      console.log('✅ Position deleted successfully');
      setPositions(positions.filter(position => position.id !== positionId));
      toast.success('Position deleted successfully');
    } catch (error) {
      console.error('❌ Unexpected error deleting position:', error);
      toast.error('Failed to delete position');
    }
  };

  const openEditModal = (position: Position) => {
    setEditingPosition(position);
    setNewPositionName(position.position_name);
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setNewPositionName('');
    setEditingPosition(null);
  };

  const selectedSport = sports.find(sport => sport.id === selectedSportId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading sports...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Positions Management</h2>
          <p className="text-gray-600">Manage positions for each sport</p>
        </div>
      </div>

      {/* Sport Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Select Sport</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full max-w-sm">
            <Label htmlFor="sport-select">Sport</Label>
            <Select value={selectedSportId} onValueChange={setSelectedSportId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a sport to manage positions" />
              </SelectTrigger>
              <SelectContent>
                {sports.map((sport) => (
                  <SelectItem key={sport.id} value={sport.id}>
                    {sport.name} ({sport.gender || 'Any'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Positions Management */}
      {selectedSportId && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Positions for {selectedSport?.name} ({positions.length})
              </CardTitle>
              <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetForm}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Position
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Position</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="position-name">Position Name</Label>
                      <Input
                        id="position-name"
                        value={newPositionName}
                        onChange={(e) => setNewPositionName(e.target.value)}
                        placeholder="Enter position name"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleAddPosition} 
                        disabled={isSubmitting || !newPositionName.trim()}
                      >
                        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Add Position
                      </Button>
                      <Button variant="outline" onClick={() => setIsAddModalOpen(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {isPositionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2">Loading positions...</span>
              </div>
            ) : positions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No positions found for this sport. Add your first position to get started.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Position Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {positions.map((position) => (
                    <TableRow key={position.id}>
                      <TableCell className="font-medium">{position.position_name}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditModal(position)}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Position</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{position.position_name}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeletePosition(position.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Position</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-position-name">Position Name</Label>
              <Input
                id="edit-position-name"
                value={newPositionName}
                onChange={(e) => setNewPositionName(e.target.value)}
                placeholder="Enter position name"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleEditPosition} 
                disabled={isSubmitting || !newPositionName.trim()}
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Update Position
              </Button>
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PositionsManagement;